import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, Subscription, finalize, take, timer } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { DisbursementCoverMemoService } from 'src/app/services/api/disbursement/disbursement-cover-memo.service';
import { DisbursementCertificationService } from 'src/app/services/api/disbursement/disbursement.service';
import { DisbursementRoundService } from 'src/app/services/api/disbursement/disbursement-round.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  DisbursementCoverMemoCertEntry,
  DisbursementCoverMemoDepartmentGroup,
  DisbursementCoverMemoGroupsResponse,
  DisbursementCoverMemoIssuePayload,
  DisbursementCoverMemoModel,
  DisbursementCoverMemoRound,
} from 'src/app/services/models/disbursement/disbursement-cover-memo.model';
import {
  DisbursementDepartment,
  DisbursementDocType,
  DisbursementPath,
  disbursementDocSectionsWithoutMerged,
  isPerTeacherDisbursementDoc,
} from 'src/app/services/models/disbursement/disbursement.model';
import { buildRoundStationSteps, RoundStepEntry } from 'src/app/services/models/disbursement/disbursement-round-steps';
import {
  DisbursementRoundStatusCode,
  ROUND_DOC_POLL_INTERVAL_MS,
  roundArtifactsPending,
} from 'src/app/services/models/disbursement/disbursement-round.model';
import {
  DisbursementStatusDisplay,
  getDisbursementRoundStatusDisplay,
} from 'src/app/services/models/disbursement/disbursement-status-display';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { SharedModule } from 'src/shared/shared.module';
import {
  buildAcademicTermNameOptions,
  buildAcademicYearOptions,
  currentAcademicTerm,
  findAcademicTerm,
  toBuddhistYear,
} from 'src/utils/academic-term-options';
import { extractBlobErrorMessage } from 'src/utils/blob-error';
import { DISBURSEMENT_DOCUMENT_OFFICER_ROLE_IDS } from 'src/utils/enum/role.enum';

// คำอธิบายหน้าสำหรับ role ที่เปิดหน้านี้ได้แต่ทำอะไรไม่ได้ (แอดมิน) — แทนคำอธิบายปกติที่
// ชวนให้กด "จัดทำใบปะหน้า" ซึ่งเป็นปุ่มที่ role นี้ไม่มี
const READ_ONLY_CAPTION =
  'ดูสถานะใบปะหน้า (บันทึกข้อความเบิกจ่ายค่าตอบแทนการสอน) ของทุกสาขาวิชา ' +
  'การจัดทำและส่งตรวจสอบเป็นสิทธิ์ของเจ้าหน้าที่สารบรรณประจำสาขาวิชา';

const DEFAULT_DOCUMENT_NUMBER = 'อว 660301.3.1.4/';
const PREVIEW_ERROR_FALLBACK = 'ยังไม่มีเอกสารรวมของรอบนี้';
const MEMBER_PREVIEW_ERROR_FALLBACK = 'ยังไม่มีเอกสาร หรือไม่สามารถแสดงเอกสารได้';
const GENERATING_DOCS_TITLE = 'กำลังจัดทำเอกสาร...';

// v2.0 phase 2 — one card state per place a round can be. Draft/returned are still
// สารบรรณ's to edit+submit; the three pending_* codes read-only + stepper; completed
// surfaces the round-merged inline preview (the ใบปะหน้า download and the ZIP download
// are both gone from this card — see the TODO on onDownloadRound).
type RoundCardState = 'draft' | 'pending' | 'returned' | 'completed' | 'cancelled';

// Which pending pool a collapsible list belongs to. ภาคปกติ has exactly one pool per
// department card; หลักสูตรนานาชาติ splits its pool by claim species (see DESIGN.md).
type CoverMemoPoolScope = 'regular' | 'course' | 'batch';

const ROUND_CARD_STATE_BY_STATUS: Record<string, RoundCardState> = {
  draft: 'draft',
  returned_to_officer: 'returned',
  pending_finance_sign: 'pending',
  pending_head: 'pending',
  pending_vice_dean_admin: 'pending',
  completed: 'completed',
  cancelled: 'cancelled',
};

// Page-local wording (defect 86eyj9wjy): เจ้าหน้าที่สารบรรณ reads a รอบส่งเบิก as "has this
// been recorded for payout yet", not as a station. Only the two states the ticket names are
// overridden — แบบร่าง and ส่งกลับแก้ไข keep their own words because neither is a payout
// state. Deliberately page-local rather than a change to SUMMARY_OVERRIDE_BY_ROUND_STATUS:
// that map is shared with round-queue/registry/registry-detail, whose readers act on the
// round instead of tracking its payout record.
const ROUND_STATUS_LABEL_OVERRIDE: Partial<Record<DisbursementRoundStatusCode, string>> = {
  pending_head: 'ยังไม่บันทึกเบิกจ่าย',
  pending_finance_sign: 'ยังไม่บันทึกเบิกจ่าย',
  pending_vice_dean_admin: 'ยังไม่บันทึกเบิกจ่าย',
  completed: 'บันทึกเบิกจ่ายสำเร็จแล้ว',
};

interface CoverMemoRoundDialogTarget {
  mode: 'create' | 'edit';
  roundId: number | null;
  academicTermId: number;
  department: DisbursementDepartment;
  path: DisbursementPath;
}

interface CoverMemoMemberPreviewState {
  loading: boolean;
  error: boolean;
  errorMessage: string | null;
  url: SafeResourceUrl | null;
}

// One previewable file of a member of the round, rendered on the fly by the
// per-document preview route (never a persisted merged read).
interface CoverMemoMemberDocRow {
  key: string;
  docType: DisbursementDocType;
  teacherUserId: number | null;
  label: string;
}

interface RoundMergedPreviewState {
  loading: boolean;
  error: boolean;
  errorMessage: string | null;
  url: SafeResourceUrl | null;
}

@Component({
  selector: 'app-disbursement-cover-memos',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './cover-memos.component.html',
  styleUrl: './cover-memos.component.scss',
})
export class CoverMemosComponent implements OnInit, OnDestroy {
  @ViewChild('submitConfirmDialog')
  submitConfirmDialog!: ConfirmDialogComponent;

  @ViewChild('deleteConfirmDialog')
  deleteConfirmDialog!: ConfirmDialogComponent;

  @ViewChild('actionSuccessDialog')
  actionSuccessDialog?: ConfirmDialogComponent;

  filterForm = inject(FormBuilder).group({
    academicTermId: [null as number | null],
    academicTerm: [null as string | null],
    academicYear: [null as string | null],
    departmentId: [null as number | null],
  });

  issueForm = inject(FormBuilder).group({
    documentNumber: [DEFAULT_DOCUMENT_NUMBER, Validators.required],
    issuedOn: [this.todayIsoDate(), Validators.required],
    // ไม่บังคับกรอก: หลายสาขายังไม่มีเบอร์ในตาราง prefill และรอบหนึ่งอาจยังไม่มีเลข PR
    // ถ้าใส่ validator สาขาเหล่านั้นจะออกใบปะหน้าไม่ได้เลย
    prNumber: [''],
    contactPhone: [''],
  });

  regularGroups = signal<DisbursementCoverMemoDepartmentGroup[]>([]);
  intlGroups = signal<DisbursementCoverMemoDepartmentGroup[]>([]);
  regularLoading = signal(false);
  intlLoading = signal(false);
  academicTermNameOptions: string[] = [];
  academicYearOptions: string[] = [];
  departmentOptions = signal<{ id: number; name: string }[]>([]);

  activeTab = signal<'regular' | 'international'>('regular');

  issueDialogOpen = signal(false);
  issueTarget = signal<CoverMemoRoundDialogTarget | null>(null);
  issueStep = signal<'select' | 'detail'>('select');
  candidateEntries = signal<DisbursementCoverMemoCertEntry[]>([]);
  selectedCertIds = signal<ReadonlySet<number>>(new Set<number>());
  // Keyed by `${certId}:${docType}[:${teacherUserId}]`, plus the single round-level
  // `round:doc_1` row that closes the list.
  memberPreviewPanels = signal<Partial<Record<string, CoverMemoMemberPreviewState>>>({});
  readonly roundMemoPreviewKey = 'round:doc_1';
  documentNumberPrefill = signal<string | null>(null);
  issueSubmitting = signal(false);
  previewLoading = signal(false);
  previewError = signal<string | null>(null);
  previewUrl = signal<SafeResourceUrl | null>(null);
  previewDirty = signal(false);
  downloadingRoundId = signal<number | null>(null);

  // One inline preview panel per round (the round's merged review artifact), keyed by round id.
  mergedPreviewPanels = signal<Partial<Record<number, RoundMergedPreviewState>>>({});

  actionLoading = signal(false);
  submitTarget = signal<DisbursementCoverMemoRound | null>(null);
  deleteTarget = signal<DisbursementCoverMemoRound | null>(null);
  successTitle = signal('');
  successMessage = signal('');

  private allRegularGroups: DisbursementCoverMemoDepartmentGroup[] = [];
  private allIntlGroups: DisbursementCoverMemoDepartmentGroup[] = [];
  private academicTerms: AcademicTermYearModel[] = [];
  private rawPreviewUrl: string | null = null;
  // Keeps raw member-PDF URLs to revoke them; sanitized SafeResourceUrl lives in memberPreviewPanels.
  private readonly rawMemberPreviewUrls = new Map<string, string>();
  private readonly rawMergedPreviewUrls = new Map<number, string>();
  // null when idle — holds the teardown of the active ADR-CE poll interval. Kept as a
  // closure because an rxjs `Subscription` type annotation trips this repo's lint env
  // (rxjs type drift, same root cause that breaks ng build locally).
  private stopRoundDocPoll: (() => void) | null = null;
  private readonly subscriptions = new Subscription();
  private readonly authService = inject(AuthService);
  private readonly coverMemoService = inject(DisbursementCoverMemoService);
  private readonly disbursementService = inject(DisbursementCertificationService);
  private readonly roundService = inject(DisbursementRoundService);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly toast = inject(ToastService);
  private readonly sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    this.listenFilters();
    this.listenIssueFormChanges();
    this.loadFilterOptions();
  }

  ngOnDestroy(): void {
    this.stopRoundDocPolling();
    this.subscriptions.unsubscribe();
    this.revokePreviewUrl();
    this.revokeAllMemberPreviews();
    this.revokeAllMergedPreviews();
  }

  // 2026-09-04 — แอดมินเปิดหน้านี้ได้แต่ "ดูอย่างเดียว" (คำตัดสินลูกค้า) ปุ่มที่เขียนข้อมูลทุกปุ่ม
  // จึงถูก **ซ่อน** ไม่ใช่ disable — บทเรียนจาก defect 86eytg5bx: ปุ่มที่กดไม่ได้ค้างอยู่บนจอ
  // ถูกรายงานเป็นบั๊กซ้ำ. ด่านจริงอยู่ที่ DisbursementCoverMemosController#reject_admin_actor!
  // ตัวนี้แค่ทำให้หน้าจอตรงกับสิทธิ์
  readonly readOnlyCaption = READ_ONLY_CAPTION;

  get canIssueCoverMemo(): boolean {
    const roleId = this.authService.getActiveRoleId();

    return roleId != null && DISBURSEMENT_DOCUMENT_OFFICER_ROLE_IDS.includes(roleId);
  }

  onTabChange(tab: 'regular' | 'international'): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
  }

  departmentName(department: DisbursementDepartment | null | undefined): string {
    if (department == null) {
      return '-';
    }

    return department.name_th ?? department.name ?? department.name_en ?? '-';
  }

  formatAmount(value: number | null | undefined): string {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);
  }

  formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return [
      String(date.getDate()).padStart(2, '0'),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(toBuddhistYear(date.getFullYear())),
    ].join('/');
  }

  // --- จัดทำใบปะหน้า (new round over the selected pending certs of a department+path) ---

  canIssueRound(group: DisbursementCoverMemoDepartmentGroup): boolean {
    return group.pending_count > 0 && !group.window_closed;
  }

  issueButtonTitle(group: DisbursementCoverMemoDepartmentGroup): string {
    if (group.window_closed) {
      return 'หมดเขตการส่งเบิกของสาขานี้แล้ว ไม่สามารถจัดทำใบปะหน้าใหม่ได้';
    }
    if (group.pending_count === 0) {
      return 'ยังไม่มีรายการที่รอดำเนินการ';
    }

    return 'จัดทำใบปะหน้า';
  }

  // --- หลักสูตรนานาชาติ pending pool, split by claim species ---
  // ADR-DA: homogeneity replaces cardinality — a round may hold N certs but never mixes species — context/decisions/DECISION_DisbursementV20.md
  // Entries from payloads predating `kind` fall on the batch side.

  coursePendingEntries(group: DisbursementCoverMemoDepartmentGroup): DisbursementCoverMemoCertEntry[] {
    return group.pending_certifications.filter((entry) => entry.kind === 'course');
  }

  batchPendingEntries(group: DisbursementCoverMemoDepartmentGroup): DisbursementCoverMemoCertEntry[] {
    return group.pending_certifications.filter((entry) => entry.kind !== 'course');
  }

  // --- รายชื่อที่รอจัดทำใบปะหน้า — one collapsible list per pool, both tabs ---
  // The set holds the pools the officer CLOSED, so every pool renders expanded without
  // seeding anything: a group arriving from loadRows() (including the refetch after each
  // mutation) is open by default, while a pool the officer collapsed stays collapsed
  // across refetches because the key is (scope, department), not an object identity.
  private readonly collapsedPools = signal<ReadonlySet<string>>(new Set<string>());

  regularPendingEntries(group: DisbursementCoverMemoDepartmentGroup): DisbursementCoverMemoCertEntry[] {
    return group.pending_certifications;
  }

  poolKey(scope: CoverMemoPoolScope, group: DisbursementCoverMemoDepartmentGroup): string {
    return `${scope}:${group.department.id}`;
  }

  poolListId(scope: CoverMemoPoolScope, group: DisbursementCoverMemoDepartmentGroup): string {
    return `disbMemoPool-${scope}-${group.department.id}`;
  }

  isPoolExpanded(key: string): boolean {
    return !this.collapsedPools().has(key);
  }

  onTogglePool(key: string): void {
    const next = new Set(this.collapsedPools());
    if (!next.delete(key)) {
      next.add(key);
    }

    this.collapsedPools.set(next);
  }

  poolToggleTitle(key: string, count: number): string {
    if (count === 0) {
      return 'ยังไม่มีรายการที่รอดำเนินการ';
    }

    return this.isPoolExpanded(key) ? 'ซ่อนรายชื่อที่รอดำเนินการ' : 'ดูรายชื่อที่รอดำเนินการ';
  }

  canIssueCourseRound(group: DisbursementCoverMemoDepartmentGroup): boolean {
    return this.coursePendingEntries(group).length > 0 && !group.window_closed;
  }

  canIssueBatchRound(group: DisbursementCoverMemoDepartmentGroup): boolean {
    return this.batchPendingEntries(group).length > 0 && !group.window_closed;
  }

  onOpenIssueDialog(group: DisbursementCoverMemoDepartmentGroup, path: DisbursementPath): void {
    if (!this.canIssueBatchRound(group)) {
      return;
    }

    const academicTermId = this.filterForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      return;
    }

    const pending = this.batchPendingEntries(group);
    this.openIssueDialog(
      { mode: 'create', roundId: null, academicTermId, department: group.department, path },
      pending,
      pending,
      {
        documentNumber: this.documentNumberPrefill() ?? DEFAULT_DOCUMENT_NUMBER,
        issuedOn: this.todayIsoDate(),
        prNumber: '',
        contactPhone: group.department.contact_phone ?? '',
      },
    );
  }

  onOpenCourseIssueDialog(group: DisbursementCoverMemoDepartmentGroup): void {
    if (!this.canIssueCourseRound(group)) {
      return;
    }

    const academicTermId = this.filterForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      return;
    }

    const pending = this.coursePendingEntries(group);
    this.openIssueDialog(
      {
        mode: 'create',
        roundId: null,
        academicTermId,
        department: group.department,
        path: 'international',
      },
      pending,
      pending,
      {
        documentNumber: this.documentNumberPrefill() ?? DEFAULT_DOCUMENT_NUMBER,
        issuedOn: this.todayIsoDate(),
        prNumber: '',
        contactPhone: group.department.contact_phone ?? '',
      },
    );
  }

  // --- แก้ไข (re-issue เลขที่/วันที่ + membership) — draft|returned_to_officer only ---

  // ADR-CG: round_merged_fresh gates แก้ไข/ส่งการเงิน until the background rebuild lands — context/decisions/DECISION_DisbursementV20.md
  canEditRound(round: DisbursementCoverMemoRound, group: DisbursementCoverMemoDepartmentGroup): boolean {
    return round.editable && !group.window_closed && round.round_merged_fresh;
  }

  editButtonTitle(round: DisbursementCoverMemoRound, group: DisbursementCoverMemoDepartmentGroup): string {
    if (group.window_closed) {
      return 'หมดเขตการส่งเบิกของสาขานี้แล้ว ไม่สามารถแก้ไขได้';
    }
    if (!round.editable) {
      return round.locked
        ? 'รอบนี้เสร็จสิ้นแล้ว ไม่สามารถแก้ไขได้'
        : 'รอบส่งเบิกนี้อยู่ระหว่างการรับรอง ไม่สามารถแก้ไขได้';
    }
    if (!round.round_merged_fresh) {
      return GENERATING_DOCS_TITLE;
    }

    return 'แก้ไข';
  }

  // A round cut from เบิกรายวิชา certs — its additions come from the course pool only,
  // because the server rejects a round mixing the two claim species.
  isCourseRound(round: DisbursementCoverMemoRound): boolean {
    return round.certifications.at(0)?.kind === 'course';
  }

  onOpenEditDialog(
    group: DisbursementCoverMemoDepartmentGroup,
    round: DisbursementCoverMemoRound,
    path: DisbursementPath,
  ): void {
    if (!this.canEditRound(round, group)) {
      return;
    }

    const academicTermId = this.filterForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      return;
    }

    const members = round.certifications;
    const courseRound = this.isCourseRound(round);
    const memberIds = new Set(members.map((entry) => entry.id));
    // The round's own species picks the pool: a batch round can never absorb a course cert,
    // and a course round can never absorb a personal one — the server rejects either mix.
    const addable = (courseRound ? this.coursePendingEntries(group) : this.batchPendingEntries(group))
      .filter((entry) => !memberIds.has(entry.id));
    this.openIssueDialog(
      { mode: 'edit', roundId: round.id, academicTermId, department: group.department, path },
      [...members, ...addable],
      members,
      {
        documentNumber: round.document_number,
        issuedOn: round.issued_on,
        prNumber: round.pr_number ?? '',
        // รอบเก่าที่ยังไม่เคยเก็บเบอร์ตกกลับไปใช้ prefill ของสาขา — ค่านี้เป็นแค่ prefill
        // ตัวที่พิมพ์ลงเอกสารคือค่าที่บันทึกไว้กับรอบเท่านั้น
        contactPhone: round.contact_phone ?? group.department.contact_phone ?? '',
      },
    );
  }

  onCloseIssueDialog(): void {
    if (this.issueSubmitting()) {
      return;
    }

    this.issueDialogOpen.set(false);
    this.issueTarget.set(null);
    this.revokePreviewUrl();
    this.revokeAllMemberPreviews();
    this.previewUrl.set(null);
    this.previewError.set(null);
    this.previewDirty.set(false);
  }

  issueDialogTitle(target: CoverMemoRoundDialogTarget): string {
    return target.mode === 'edit' ? 'แก้ไขใบปะหน้า' : 'จัดทำใบปะหน้า';
  }

  issueDialogMetaLines(target: CoverMemoRoundDialogTarget): string[] {
    return [`สาขาวิชา: ${this.departmentName(target.department)}`];
  }

  // --- Step 1: เลือกรายการที่จะเข้ารอบ ---

  isCertSelected(entry: DisbursementCoverMemoCertEntry): boolean {
    return this.selectedCertIds().has(entry.id);
  }

  allCandidatesSelected(): boolean {
    const entries = this.candidateEntries();
    return entries.length > 0 && entries.every((entry) => this.selectedCertIds().has(entry.id));
  }

  someCandidatesSelected(): boolean {
    return this.selectedCertIds().size > 0 && !this.allCandidatesSelected();
  }

  selectedEntries(): DisbursementCoverMemoCertEntry[] {
    return this.candidateEntries().filter((entry) => this.selectedCertIds().has(entry.id));
  }

  selectedCount(): number {
    return this.selectedEntries().length;
  }

  selectedTotalAmount(): number {
    return this.selectedEntries().reduce((total, entry) => total + entry.amount, 0);
  }

  onToggleCert(entry: DisbursementCoverMemoCertEntry): void {
    const next = new Set(this.selectedCertIds());
    if (next.has(entry.id)) {
      next.delete(entry.id);
    } else {
      next.add(entry.id);
    }

    this.selectedCertIds.set(next);
    this.previewDirty.set(true);
    this.collapseMemberPreviewsOf(entry.id);
  }

  onToggleSelectAll(): void {
    const entries = this.candidateEntries();
    this.selectedCertIds.set(
      this.allCandidatesSelected() ? new Set<number>() : new Set(entries.map((entry) => entry.id)),
    );
    this.previewDirty.set(true);
    this.revokeAllMemberPreviews();
  }

  canProceedToDetail(): boolean {
    return this.selectedCertIds().size > 0;
  }

  onNextStep(): void {
    if (!this.canProceedToDetail()) {
      return;
    }

    this.issueStep.set('detail');
  }

  onBackToSelect(): void {
    this.issueStep.set('select');
  }

  // --- Step 2: เอกสารในรอบ — ADR-CI: doc_1 is a single round-level row closing the list — context/decisions/DECISION_DisbursementV20.md ---

  memberDocRows(entry: DisbursementCoverMemoCertEntry): CoverMemoMemberDocRow[] {
    const path = this.issueTarget()?.path ?? 'regular';

    return disbursementDocSectionsWithoutMerged(path)
      .filter((spec) => spec.docType !== 'doc_1')
      .flatMap((spec) => {
        if (path !== 'international' || !isPerTeacherDisbursementDoc(spec.docType)) {
          return [this.buildMemberDocRow(entry, spec.docType, null, spec.title)];
        }

        const teachers = entry.teachers ?? [];
        if (teachers.length === 0) {
          return [this.buildMemberDocRow(entry, spec.docType, null, spec.title)];
        }

        // doc_7 is per (teacher, term) — byte-identical whichever cert renders it — so a
        // teacher shared across the selected certs lists it under their first cert only.
        // doc_3/doc_4 stay per cert: their figures come from that cert's own snapshot.
        const covered = spec.docType === 'doc_7' ? this.teachersCoveredBefore(entry) : null;

        return teachers
          .filter((teacher) => covered?.has(teacher.user_id) !== true)
          .map((teacher) => this.buildMemberDocRow(
            entry,
            spec.docType,
            teacher.user_id,
            `${spec.title} — ${teacher.full_name ?? '-'}`,
          ));
      });
  }

  private teachersCoveredBefore(entry: DisbursementCoverMemoCertEntry): ReadonlySet<number> {
    const covered = new Set<number>();
    for (const candidate of this.selectedEntries()) {
      if (candidate.id === entry.id) {
        break;
      }
      for (const teacher of candidate.teachers ?? []) {
        covered.add(teacher.user_id);
      }
    }

    return covered;
  }

  isMemberPreviewExpanded(key: string): boolean {
    return this.memberPreviewPanels()[key] != null;
  }

  isMemberPreviewLoading(key: string): boolean {
    return this.memberPreviewPanels()[key]?.loading ?? false;
  }

  memberPreviewHasError(key: string): boolean {
    return this.memberPreviewPanels()[key]?.error ?? false;
  }

  memberPreviewErrorMessage(key: string): string {
    return this.memberPreviewPanels()[key]?.errorMessage ?? MEMBER_PREVIEW_ERROR_FALLBACK;
  }

  memberPreviewUrl(key: string): SafeResourceUrl | null {
    return this.memberPreviewPanels()[key]?.url ?? null;
  }

  onToggleMemberDocPreview(entry: DisbursementCoverMemoCertEntry, row: CoverMemoMemberDocRow): void {
    if (this.isMemberPreviewExpanded(row.key)) {
      this.collapseMemberPreview(row.key);
      return;
    }

    this.expandDocPreview(row.key, entry.id, row.docType, row.teacherUserId);
  }

  // The round's บันทึกข้อความ closes the เอกสารในรอบ list, mirroring where doc_1 sits in
  // the merged book. It is a term-level document — the preview route short-circuits doc_1
  // to the term's OverloadMemo, so any member of the round streams the same file.
  onToggleRoundMemoPreview(): void {
    const key = this.roundMemoPreviewKey;
    if (this.isMemberPreviewExpanded(key)) {
      this.collapseMemberPreview(key);
      return;
    }

    const entry = this.selectedEntries().at(0);
    if (entry == null) {
      return;
    }

    this.expandDocPreview(key, entry.id, 'doc_1', null);
  }

  // "สร้าง" — (re)generate the round PDF preview from the CURRENT form values +
  // selection. Persists nothing; the backend 422s when the round window is
  // closed, the selection is empty, or a selected item is no longer claimable —
  // that copy is surfaced verbatim in the preview area.
  onGenerate(): void {
    if (this.issueForm.invalid || this.previewLoading() || !this.canProceedToDetail()) {
      this.issueForm.markAllAsTouched();
      return;
    }

    this.loadPreview();
  }

  onConfirmIssue(): void {
    const target = this.issueTarget();
    if (target == null || this.issueSubmitting() || !this.canProceedToDetail()) {
      return;
    }

    if (this.issueForm.invalid) {
      this.issueForm.markAllAsTouched();
      return;
    }

    const request: Observable<DisbursementCoverMemoModel> = target.mode === 'edit' && target.roundId != null
      ? this.coverMemoService.update(target.roundId, {
        document_number: this.trimmedIssueField('documentNumber'),
        issued_on: this.issueForm.getRawValue().issuedOn ?? '',
        pr_number: this.trimmedIssueField('prNumber'),
        contact_phone: this.trimmedIssueField('contactPhone'),
        certification_ids: this.sortedSelectedIds(),
      })
      : this.coverMemoService.create(this.buildIssuePayload(target));

    this.issueSubmitting.set(true);
    request.pipe(finalize(() => this.issueSubmitting.set(false))).subscribe({
      next: () => {
        this.issueDialogOpen.set(false);
        this.issueTarget.set(null);
        this.revokePreviewUrl();
        this.revokeAllMemberPreviews();
        // The edited round's merged volume is being rebuilt — an expanded panel would keep
        // showing the blob fetched before the edit, so drop every panel and let the officer
        // reopen once round_merged_fresh flips back.
        this.revokeAllMergedPreviews();
        this.previewUrl.set(null);
        this.loadRows();
        this.showSuccess(
          target.mode === 'edit' ? 'แก้ไขใบปะหน้าสำเร็จ' : 'จัดทำใบปะหน้าสำเร็จ',
          target.mode === 'edit' ? 'แก้ไขใบปะหน้าเรียบร้อยแล้ว' : 'จัดทำใบปะหน้าเรียบร้อยแล้ว',
        );
      },
      // The server is the arbiter of who may still join a round — reload the
      // groups so a reopened dialog sees the fresh pending lists.
      error: (err) => {
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
        this.loadRows();
      },
    });
  }

  // --- Per-round card state (draft/pending/returned/completed) ---

  roundCardState(round: DisbursementCoverMemoRound): RoundCardState {
    return round.status_code != null ? (ROUND_CARD_STATE_BY_STATUS[round.status_code] ?? 'draft') : 'draft';
  }

  // 'summary' on purpose, even though DOCUMENT_OFFICER reads the detailed audience
  // everywhere else: on this page the four stations already show as a stepper right
  // below the chip, so the chip itself must not restate the station. The tone still
  // comes from the shared mapper — only the wording is page-local
  // (ROUND_STATUS_LABEL_OVERRIDE), which keeps round-queue and registry-detail on
  // their per-station labels and colours every state identically to them.
  // Both tabs render this: ภาคปกติ and นานาชาติ share the same round card.
  roundStatusDisplay(round: DisbursementCoverMemoRound): DisbursementStatusDisplay {
    const display = getDisbursementRoundStatusDisplay(round.status_code, 'summary');
    const label = round.status_code != null ? ROUND_STATUS_LABEL_OVERRIDE[round.status_code] : undefined;

    return label != null ? { ...display, label } : display;
  }

  // The completed card's own note repeats the chip's word, so it reads from the same
  // override instead of hard-coding a second copy of it.
  completedNoteLabel(round: DisbursementCoverMemoRound): string {
    return this.roundStatusDisplay(round).label;
  }

  // ลบ — draft rounds only, no window_closed gate (deleting a draft never touches a
  // closed window's certified state).
  canDeleteRound(round: DisbursementCoverMemoRound): boolean {
    return (round.status_code ?? 'draft') === 'draft';
  }

  teacherCell(round: DisbursementCoverMemoRound): string {
    return round.single_teacher_name ?? `${round.teacher_count} คน`;
  }

  // Mirrors Disbursement::Rounds::StationResolver — same four stations/order/labels, driven
  // off the round's own sign-date columns so this can never disagree with the ใบปะหน้า.
  roundSteps(round: DisbursementCoverMemoRound): RoundStepEntry[] {
    return buildRoundStationSteps(round);
  }

  // --- ส่งตรวจสอบและรับรอง (draft|returned_to_officer -> pending_head) ---

  canSubmitRound(round: DisbursementCoverMemoRound, group: DisbursementCoverMemoDepartmentGroup): boolean {
    return round.editable && !group.window_closed && round.round_merged_fresh;
  }

  submitButtonLabel(_round?: DisbursementCoverMemoRound): string {
    return 'ส่งตรวจสอบและรับรอง';
  }

  submitButtonTitle(round: DisbursementCoverMemoRound, group: DisbursementCoverMemoDepartmentGroup): string {
    if (group.window_closed) {
      return 'หมดเขตการส่งเบิกของสาขานี้แล้ว';
    }
    if (!round.round_merged_fresh) {
      return GENERATING_DOCS_TITLE;
    }

    return '';
  }

  onOpenSubmitConfirm(round: DisbursementCoverMemoRound, group: DisbursementCoverMemoDepartmentGroup): void {
    if (!this.canSubmitRound(round, group)) {
      return;
    }

    this.submitTarget.set(round);
    this.submitConfirmDialog.open();
  }

  onConfirmSubmit(): void {
    const round = this.submitTarget();
    if (round == null) {
      return;
    }

    this.actionLoading.set(true);
    this.roundService.submit(round.id).pipe(finalize(() => this.actionLoading.set(false))).subscribe({
      next: () => {
        this.submitConfirmDialog.close();
        this.submitTarget.set(null);
        this.loadRows();
        this.showSuccess('ส่งตรวจสอบและรับรองสำเร็จ', 'ส่งตรวจสอบและรับรองเรียบร้อยแล้ว');
      },
      error: (err) => {
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  // --- ลบใบปะหน้า (draft rounds only) ---

  onOpenDeleteConfirm(round: DisbursementCoverMemoRound): void {
    if (!this.canDeleteRound(round)) {
      return;
    }

    this.deleteTarget.set(round);
    this.deleteConfirmDialog.open();
  }

  onConfirmDelete(): void {
    const round = this.deleteTarget();
    if (round == null) {
      return;
    }

    this.actionLoading.set(true);
    this.coverMemoService.delete(round.id).pipe(finalize(() => this.actionLoading.set(false))).subscribe({
      next: () => {
        this.deleteConfirmDialog.close();
        this.deleteTarget.set(null);
        this.loadRows();
        this.showSuccess('ลบใบปะหน้าสำเร็จ', 'ลบใบปะหน้าเรียบร้อยแล้ว');
      },
      error: (err) => {
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  // --- เอกสารรวมทั้งรอบ (inline preview, same artifact/pattern as the round-queue page) ---

  isMergedPreviewExpanded(roundId: number): boolean {
    return this.mergedPreviewPanels()[roundId] != null;
  }

  isMergedPreviewLoading(roundId: number): boolean {
    return this.mergedPreviewPanels()[roundId]?.loading ?? false;
  }

  mergedPreviewHasError(roundId: number): boolean {
    return this.mergedPreviewPanels()[roundId]?.error ?? false;
  }

  mergedPreviewErrorMessage(roundId: number): string | null {
    return this.mergedPreviewPanels()[roundId]?.errorMessage ?? null;
  }

  mergedPreviewUrl(roundId: number): SafeResourceUrl | null {
    return this.mergedPreviewPanels()[roundId]?.url ?? null;
  }

  onToggleMergedPreview(round: DisbursementCoverMemoRound): void {
    if (this.isMergedPreviewExpanded(round.id)) {
      this.collapseMergedPreview(round.id);
      return;
    }

    this.expandMergedPreview(round);
  }

  // --- ดาวน์โหลดใบปะหน้า (reference download — presence-gated only) ---

  // TODO: ปุ่มดาวน์โหลดใบปะหน้าถูกซ่อนตามนโยบาย "โหลดทีเดียวตอน Export" — ยังไม่ตัดสินใจว่าจะลบ endpoint ทิ้งไหม
  canDownloadRound(round: DisbursementCoverMemoRound): boolean {
    return round.has_pdf;
  }

  onDownloadRound(round: DisbursementCoverMemoRound): void {
    this.download(round, this.coverMemoService.download(round.id), `cover_memo_${round.id}.pdf`);
  }

  isDownloadingRound(round: DisbursementCoverMemoRound): boolean {
    return this.downloadingRoundId() === round.id;
  }

  private listenFilters(): void {
    this.subscriptions.add(
      this.filterForm.controls.academicYear.valueChanges.subscribe(() => this.onAcademicYearChanged()),
    );
    this.subscriptions.add(
      this.filterForm.controls.academicTerm.valueChanges.subscribe(() => {
        this.syncAcademicTermId();
        this.loadRows();
      }),
    );
    this.subscriptions.add(
      this.filterForm.controls.departmentId.valueChanges.subscribe(() => this.applyDepartmentFilter()),
    );
  }

  // เลขที่/วันที่ edits mark the rendered preview stale until reloaded.
  private listenIssueFormChanges(): void {
    this.subscriptions.add(
      this.issueForm.valueChanges.subscribe(() => {
        if (this.issueDialogOpen()) {
          this.previewDirty.set(true);
        }
      }),
    );
  }

  private onAcademicYearChanged(): void {
    const { academicYear, academicTerm } = this.filterForm.getRawValue();
    this.academicTermNameOptions = buildAcademicTermNameOptions(this.academicTerms, academicYear);
    const nextTerm = academicTerm != null && this.academicTermNameOptions.includes(academicTerm)
      ? academicTerm
      : this.academicTermNameOptions[0] ?? null;
    this.filterForm.patchValue({ academicTerm: nextTerm }, { emitEvent: false });
    this.syncAcademicTermId();
    this.loadRows();
  }

  private syncAcademicTermId(): void {
    const { academicTerm, academicYear } = this.filterForm.getRawValue();
    const resolved = findAcademicTerm(this.academicTerms, academicTerm, academicYear);
    this.filterForm.patchValue({ academicTermId: resolved?.id ?? null }, { emitEvent: false });
  }

  private loadFilterOptions(): void {
    this.studyPeriodService.getAcademicTermYears().pipe(take(1)).subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.academicYearOptions = buildAcademicYearOptions(terms);
        const defaultTerm = currentAcademicTerm(terms, this.authService.currentUser()?.academic_term);
        const defaultYear = defaultTerm != null ? String(toBuddhistYear(defaultTerm.year)) : null;
        this.academicTermNameOptions = buildAcademicTermNameOptions(terms, defaultYear);
        this.filterForm.patchValue(
          {
            academicYear: defaultYear,
            academicTerm: defaultTerm != null ? String(defaultTerm.name).trim() : null,
            academicTermId: defaultTerm?.id ?? null,
          },
          { emitEvent: false },
        );
        this.loadRows();
      },
      error: (err) => console.error(err),
    });
  }

  private showSuccess(title: string, message: string): void {
    this.successTitle.set(title);
    this.successMessage.set(message);
    this.actionSuccessDialog?.open();
  }

  private loadRows(): void {
    this.documentNumberPrefill.set(null);
    this.loadRegularGroups();
    this.loadIntlGroups();
  }

  private loadRegularGroups(): void {
    const academicTermId = this.filterForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      this.allRegularGroups = [];
      this.refreshDepartmentOptions();
      this.applyDepartmentFilter();
      return;
    }

    this.regularLoading.set(true);
    this.coverMemoService.getRows({ academic_term_id: academicTermId }).pipe(
      finalize(() => this.regularLoading.set(false)),
    ).subscribe({
      next: (response) => this.onGroupsLoaded(response, 'regular'),
      error: (err) => {
        console.error(err);
        this.allRegularGroups = [];
        this.refreshDepartmentOptions();
        this.applyDepartmentFilter();
      },
    });
  }

  private loadIntlGroups(): void {
    const academicTermId = this.filterForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      this.allIntlGroups = [];
      this.refreshDepartmentOptions();
      this.applyDepartmentFilter();
      return;
    }

    this.intlLoading.set(true);
    this.coverMemoService.getInternationalRows({ academic_term_id: academicTermId }).pipe(
      finalize(() => this.intlLoading.set(false)),
    ).subscribe({
      next: (response) => this.onGroupsLoaded(response, 'international'),
      error: (err) => {
        console.error(err);
        this.allIntlGroups = [];
        this.refreshDepartmentOptions();
        this.applyDepartmentFilter();
      },
    });
  }

  private onGroupsLoaded(response: DisbursementCoverMemoGroupsResponse, path: DisbursementPath): void {
    if (path === 'international') {
      this.allIntlGroups = response.groups;
    } else {
      this.allRegularGroups = response.groups;
    }
    // เลขที่ prefill is term-level (the term's บันทึกข้อความ) — both tabs' responses
    // carry the same value, so keep the first non-empty one either of them returns.
    if (this.documentNumberPrefill() == null && (response.document_number_prefill ?? '') !== '') {
      this.documentNumberPrefill.set(response.document_number_prefill);
    }
    this.refreshDepartmentOptions();
    this.applyDepartmentFilter();
    this.syncRoundDocPolling();
  }

  // ADR-CE: round artifacts build async — poll rows until every round has its files, no manual reload — context/decisions/DECISION_DisbursementV20.md
  // Idempotent: safe to call after every rows load; stops itself once nothing is pending.
  private syncRoundDocPolling(): void {
    const pending = [...this.allRegularGroups, ...this.allIntlGroups]
      .some((group) => group.rounds.some((round) => roundArtifactsPending(round)));

    if (!pending) {
      this.stopRoundDocPolling();
      return;
    }
    if (this.stopRoundDocPoll != null) {
      return;
    }

    const subscription = timer(ROUND_DOC_POLL_INTERVAL_MS, ROUND_DOC_POLL_INTERVAL_MS)
      .subscribe(() => this.reloadRowsQuietly());
    this.stopRoundDocPoll = () => {
      subscription.unsubscribe();
    };
  }

  private stopRoundDocPolling(): void {
    this.stopRoundDocPoll?.();
    this.stopRoundDocPoll = null;
  }

  // No loading spinners: the cards must not flicker every poll tick.
  private reloadRowsQuietly(): void {
    const academicTermId = this.filterForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      this.stopRoundDocPolling();
      return;
    }

    this.coverMemoService.getRows({ academic_term_id: academicTermId }).pipe(take(1)).subscribe({
      next: (response) => this.onGroupsLoaded(response, 'regular'),
      error: (err) => console.error(err),
    });
    this.coverMemoService.getInternationalRows({ academic_term_id: academicTermId }).pipe(take(1)).subscribe({
      next: (response) => this.onGroupsLoaded(response, 'international'),
      error: (err) => console.error(err),
    });
  }

  private refreshDepartmentOptions(): void {
    const byId = new Map<number, string>();

    [...this.allRegularGroups, ...this.allIntlGroups].forEach((group) => {
      if (!byId.has(group.department.id)) {
        byId.set(group.department.id, this.departmentName(group.department));
      }
    });

    this.departmentOptions.set(
      Array.from(byId.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'th')),
    );
  }

  private applyDepartmentFilter(): void {
    const departmentId = this.filterForm.getRawValue().departmentId;
    this.regularGroups.set(this.filterGroups(this.allRegularGroups, departmentId));
    this.intlGroups.set(this.filterGroups(this.allIntlGroups, departmentId));
  }

  private filterGroups(
    groups: DisbursementCoverMemoDepartmentGroup[],
    departmentId: number | null,
  ): DisbursementCoverMemoDepartmentGroup[] {
    return departmentId == null
      ? groups
      : groups.filter((group) => group.department.id === departmentId);
  }

  private loadPreview(): void {
    const target = this.issueTarget();
    if (target == null) {
      return;
    }

    this.previewLoading.set(true);
    this.previewError.set(null);
    this.coverMemoService.preview(this.buildIssuePayload(target)).pipe(
      take(1),
      finalize(() => this.previewLoading.set(false)),
    ).subscribe({
      next: (blob) => {
        this.revokePreviewUrl();
        this.rawPreviewUrl = window.URL.createObjectURL(blob);
        this.previewUrl.set(
          this.sanitizer.bypassSecurityTrustResourceUrl(`${this.rawPreviewUrl}#toolbar=0&navpanes=0`),
        );
        this.previewDirty.set(false);
      },
      error: (err) => {
        console.error(err);
        this.revokePreviewUrl();
        this.previewUrl.set(null);
        void extractBlobErrorMessage(err, this.extractErrorMessage(err)).then((message) => {
          this.previewError.set(message);
          this.toast.toastError(message);
        });
      },
    });
  }

  private buildIssuePayload(target: CoverMemoRoundDialogTarget): DisbursementCoverMemoIssuePayload {
    return {
      academic_term_id: target.academicTermId,
      department_id: target.department.id,
      path: target.path,
      document_number: this.trimmedIssueField('documentNumber'),
      issued_on: this.issueForm.getRawValue().issuedOn ?? '',
      pr_number: this.trimmedIssueField('prNumber'),
      contact_phone: this.trimmedIssueField('contactPhone'),
      certification_ids: this.sortedSelectedIds(),
      ...(target.mode === 'edit' && target.roundId != null ? { cover_memo_id: target.roundId } : {}),
    };
  }

  private sortedSelectedIds(): number[] {
    return Array.from(this.selectedCertIds()).sort((a, b) => a - b);
  }

  // prefill ต้องมีครบทุก key เพราะ reset() ล้าง control ที่ไม่ได้ระบุทิ้ง — ตกไปแค่ key เดียว
  // โหมด "แก้ไข" จะล้างค่าที่บันทึกไว้เงียบ ๆ โดยที่โหมด "สร้าง" ยังดูปกติ
  private openIssueDialog(
    target: CoverMemoRoundDialogTarget,
    entries: DisbursementCoverMemoCertEntry[],
    preChecked: DisbursementCoverMemoCertEntry[],
    prefill: { documentNumber: string; issuedOn: string; prNumber: string; contactPhone: string },
  ): void {
    this.issueTarget.set(target);
    this.startSelection(entries, preChecked);
    this.issueForm.reset(prefill);
    this.previewDirty.set(false);
    this.previewError.set(null);
    this.issueDialogOpen.set(true);
  }

  private startSelection(
    entries: DisbursementCoverMemoCertEntry[],
    preChecked: DisbursementCoverMemoCertEntry[],
  ): void {
    this.revokeAllMemberPreviews();
    this.candidateEntries.set(entries);
    this.selectedCertIds.set(new Set(preChecked.map((entry) => entry.id)));
    this.issueStep.set('select');
  }

  private buildMemberDocRow(
    entry: DisbursementCoverMemoCertEntry,
    docType: DisbursementDocType,
    teacherUserId: number | null,
    label: string,
  ): CoverMemoMemberDocRow {
    return {
      key: this.memberPreviewKey(entry.id, docType, teacherUserId),
      docType,
      teacherUserId,
      label,
    };
  }

  private memberPreviewKey(
    certId: number,
    docType: DisbursementDocType,
    teacherUserId: number | null,
  ): string {
    return teacherUserId != null ? `${certId}:${docType}:${teacherUserId}` : `${certId}:${docType}`;
  }

  // One Chromium render per expanded row server-side — never eager-load the whole list.
  private expandDocPreview(
    key: string,
    certId: number,
    docType: DisbursementDocType,
    teacherUserId: number | null,
  ): void {
    this.patchMemberPreviewPanels(key, { loading: true, error: false, errorMessage: null, url: null });
    this.disbursementService.previewDocument(certId, docType, teacherUserId).pipe(take(1)).subscribe({
      next: (blob) => this.applyMemberPreviewBlob(key, blob),
      error: (err) => this.applyMemberPreviewError(key, err),
    });
  }

  private applyMemberPreviewBlob(key: string, blob: Blob): void {
    const rawUrl = window.URL.createObjectURL(blob);
    this.rawMemberPreviewUrls.set(key, rawUrl);
    this.patchMemberPreviewPanels(key, {
      loading: false,
      error: false,
      errorMessage: null,
      url: this.sanitizer.bypassSecurityTrustResourceUrl(`${rawUrl}#toolbar=0&navpanes=0`),
    });
  }

  private applyMemberPreviewError(key: string, err: unknown): void {
    console.error(err);
    void extractBlobErrorMessage(err, MEMBER_PREVIEW_ERROR_FALLBACK).then((errorMessage) => {
      this.patchMemberPreviewPanels(key, { loading: false, error: true, errorMessage, url: null });
    });
  }

  private collapseMemberPreview(key: string): void {
    if (!this.isMemberPreviewExpanded(key)) {
      return;
    }

    this.revokeMemberPreview(key);
    this.memberPreviewPanels.update((panels) => {
      const next = { ...panels };
      delete next[key];
      return next;
    });
  }

  // Every doc/teacher row of one cert — used when the cert leaves the selection. The
  // round-level `round:doc_1` row keys off no cert id, so it is never swept up here.
  private collapseMemberPreviewsOf(certId: number): void {
    const prefix = `${certId}:`;
    const keys = [
      ...Object.keys(this.memberPreviewPanels()),
      ...this.rawMemberPreviewUrls.keys(),
    ].filter((key) => key.startsWith(prefix));

    if (keys.length === 0) {
      return;
    }

    keys.forEach((key) => this.revokeMemberPreview(key));
    this.memberPreviewPanels.update((panels) => {
      const next = { ...panels };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  }

  private patchMemberPreviewPanels(key: string, state: CoverMemoMemberPreviewState): void {
    this.memberPreviewPanels.update((panels) => ({ ...panels, [key]: state }));
  }

  private revokeMemberPreview(key: string): void {
    const rawUrl = this.rawMemberPreviewUrls.get(key);
    if (rawUrl != null) {
      window.URL.revokeObjectURL(rawUrl);
      this.rawMemberPreviewUrls.delete(key);
    }
  }

  private revokeAllMemberPreviews(): void {
    this.rawMemberPreviewUrls.forEach((rawUrl) => window.URL.revokeObjectURL(rawUrl));
    this.rawMemberPreviewUrls.clear();
    this.memberPreviewPanels.set({});
  }

  private expandMergedPreview(round: DisbursementCoverMemoRound): void {
    this.patchMergedPreviewPanels(round.id, { loading: true, error: false, errorMessage: null, url: null });
    this.roundService.downloadMerged(round.id).pipe(take(1)).subscribe({
      next: (blob) => {
        const rawUrl = window.URL.createObjectURL(blob);
        this.rawMergedPreviewUrls.set(round.id, rawUrl);
        this.patchMergedPreviewPanels(round.id, {
          loading: false,
          error: false,
          errorMessage: null,
          url: this.sanitizer.bypassSecurityTrustResourceUrl(`${rawUrl}#toolbar=0&navpanes=0`),
        });
      },
      error: (err) => {
        console.error(err);
        void extractBlobErrorMessage(err, PREVIEW_ERROR_FALLBACK).then((errorMessage) => {
          this.patchMergedPreviewPanels(round.id, { loading: false, error: true, errorMessage, url: null });
        });
      },
    });
  }

  private collapseMergedPreview(roundId: number): void {
    this.revokeMergedPreview(roundId);
    this.mergedPreviewPanels.update((panels) => {
      const next = { ...panels };
      delete next[roundId];
      return next;
    });
  }

  private patchMergedPreviewPanels(roundId: number, state: RoundMergedPreviewState): void {
    this.mergedPreviewPanels.update((panels) => ({ ...panels, [roundId]: state }));
  }

  private revokeMergedPreview(roundId: number): void {
    const rawUrl = this.rawMergedPreviewUrls.get(roundId);
    if (rawUrl != null) {
      window.URL.revokeObjectURL(rawUrl);
      this.rawMergedPreviewUrls.delete(roundId);
    }
  }

  private revokeAllMergedPreviews(): void {
    this.rawMergedPreviewUrls.forEach((rawUrl) => window.URL.revokeObjectURL(rawUrl));
    this.rawMergedPreviewUrls.clear();
    this.mergedPreviewPanels.set({});
  }

  private trimmedIssueField(control: 'documentNumber' | 'prNumber' | 'contactPhone'): string {
    return String(this.issueForm.getRawValue()[control] ?? '').trim();
  }

  private download(round: DisbursementCoverMemoRound, request: Observable<Blob>, fileName: string): void {
    if (this.downloadingRoundId() != null) {
      return;
    }

    this.downloadingRoundId.set(round.id);
    request.pipe(
      take(1),
      finalize(() => this.downloadingRoundId.set(null)),
    ).subscribe({
      next: (blob) => this.downloadBlob(blob, fileName),
      error: (err) => {
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private revokePreviewUrl(): void {
    if (this.rawPreviewUrl != null) {
      window.URL.revokeObjectURL(this.rawPreviewUrl);
      this.rawPreviewUrl = null;
    }
  }

  private todayIsoDate(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private extractErrorMessage(err: unknown): string {
    if (err !== null && typeof err === 'object' && 'error' in err) {
      const error = (err as { error?: unknown }).error;
      if (error !== null && typeof error === 'object' && 'errors' in error) {
        const errors = (error as { errors?: unknown }).errors;
        if (Array.isArray(errors) && errors.length > 0) {
          return String(errors[0]);
        }
        if (typeof errors === 'string') {
          return errors;
        }
      }
    }

    return 'ไม่สามารถดำเนินการได้';
  }
}
